import { classNames } from '@dimjs/utils';
import { animated, useSpring } from '@react-spring/web';
import { hooks } from '@wove/react';
import { CSSProperties, FC, Fragment, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Mask } from '../mask';
import { GetContainer, resolveContainer } from '../utils/get-container';
import { CommonPropsWithChildren, withNativeProps } from '../utils/native-props';
import { getZIndex } from '../utils/z-index';
import './style.less';
import { useShouldRender } from './utils/use-should-render';

const classPrefix = `v-modal`;
export type ModalProps = {
  visible?: boolean;
  mask?: boolean;
  onMaskClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  destroyOnClose?: boolean;
  forceRender?: boolean;
  getContainer?: GetContainer;
  onBeforeOpen?: () => void;
  onAfterOpen?: () => void;
  onBeforeClose?: () => void;
  onAfterClose?: () => void;
  bodyClassName?: string;
  bodyStyle?: CSSProperties;
  maskClassName?: string;
  maskStyle?: CSSProperties;
  onClose?: (operate?: 'mask' | 'closeIcon') => void;
  closeByMask?: boolean;
  closeIcon?: boolean;
  title?: string | JSX.Element;
  duration?: number;
  clickStopPropagation?: boolean;
} & CommonPropsWithChildren<{ '--modal-border-radius': CSSProperties['borderRadius'] }>;

const ModalContent: FC<ModalProps> = (props) => {
  return (
    <Fragment>
      {props.closeIcon && (
        <div className={`${classPrefix}-body-close`} onClick={props.onClose?.bind(null, 'closeIcon')}></div>
      )}
      {props.title && <div className={`${classPrefix}-body-title`}>{props.title}</div>}
      {props.children}
    </Fragment>
  );
};

export const Modal: FC<ModalProps> = (props) => {
  const maskZIndex = useRef<number>();
  const bodyZIndex = useRef<number>();
  const ref = useRef<HTMLDivElement>(null);

  const updateZindex = hooks.useCallbackRef(() => {
    maskZIndex.current = getZIndex();
    bodyZIndex.current = getZIndex();
  });

  const [visible, setVisible] = useState(() => {
    if (props.visible) {
      updateZindex();
    }
    return props.visible || false;
  });

  const shouldRender = useShouldRender(visible, props.forceRender, props.destroyOnClose);

  const onBeforeOpenHandle = hooks.useCallbackRef(() => {
    updateZindex();
    props.onBeforeOpen?.();
    setVisible(true);
  });

  const onAfterOpenHandle = hooks.useCallbackRef(() => {
    props.onAfterOpen?.();
  });

  const onBeforeCloseHandle = hooks.useCallbackRef(() => {
    props.onBeforeClose?.();
  });

  const onAfterCloseHandle = hooks.useCallbackRef(() => {
    setVisible(false);
    props.onAfterClose?.();
  });

  const style = useSpring({
    scale: props.visible ? 1 : 0.8,
    opacity: props.visible ? 1 : 0,
    config: {
      mass: 1,
      tension: 250,
      friction: 20,
      clamp: true,
      duration: props.duration,
    },
    onStart: () => {
      if (!visible) {
        onBeforeOpenHandle();
      } else {
        onBeforeCloseHandle();
      }
    },
    onRest: () => {
      if (props.visible) {
        onAfterOpenHandle();
      } else {
        onAfterCloseHandle();
      }
    },
  });

  const onClick = hooks.useCallbackRef((event) => {
    if (props.clickStopPropagation) {
      event.stopPropagation();
    }
  });

  const onMaskClick = hooks.useCallbackRef((event) => {
    event.stopPropagation();
    if (props.onMaskClick) {
      props.onMaskClick(event as React.MouseEvent<HTMLDivElement, MouseEvent>);
    } else {
      if (props.closeByMask) {
        props.onClose?.('mask');
      }
    }
  });

  if (!visible) {
    document.body.classList.remove('overflow-hidden');
  } else {
    document.body.classList.add('overflow-hidden');
  }

  const node = withNativeProps(
    props,
    <div className={classPrefix} onClick={onClick} style={{ display: visible ? 'unset' : 'none' }}>
      {props.mask && (
        <Mask
          visible={props.visible}
          onMaskClick={onMaskClick}
          className={props.maskClassName}
          style={{ zIndex: maskZIndex.current, ...props.maskStyle }}
        />
      )}
      <div
        className={classNames(`${classPrefix}-body`, props.bodyClassName)}
        style={{ zIndex: bodyZIndex.current, ...props.bodyStyle }}
      >
        <animated.div className={`${classPrefix}-body-content`} style={style} ref={ref}>
          {shouldRender && <ModalContent {...props} />}
        </animated.div>
      </div>
    </div>,
  );
  if (props.getContainer) {
    const container = resolveContainer(props.getContainer);
    return createPortal(node, container);
  } else {
    return node;
  }
};

Modal.defaultProps = {
  visible: false,
  getContainer: () => document.body,
  mask: true,
  closeByMask: true,
  clickStopPropagation: true,
};
